import {
	getLeCoffreAccessTokenCookieForContext,
	getLeCoffreCustomerAccessTokenCookie,
	getLeCoffreNotaryAccessTokenCookie,
	getLeCoffreNotaryRefreshTokenCookie,
	getLeCoffreRefreshTokenCookieForContext,
	getLeCoffreThirdPartyAccessTokenCookie,
	resolveLeCoffreAuthContextFromEndpoint,
	shouldUseActiveSessionTokenForEndpoint,
	type LeCoffreAuthContext,
} from "@Front/Utils/lecoffreSessionCookieHelpers";
import { resolveRouteContextFamily } from "@Front/Utils/routeContextRegistry";
import { getActiveSessionFamily } from "@Front/Utils/lecoffreActiveSessionFamily";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { CustomerStore } from "@Front/Stores/CustomerStore";
import { ThirdPartyStore } from "@Front/Stores/ThirdPartyStore";
import { UserStore } from "@Front/Stores/UserStore";
import { resolveConsistentAccessToken } from "@Front/Api/helpers/authAccessTokenConsistencyHelpers";

type AccessTokenReader = () => string | null;

function getBrowserPathnameForAuthContext(): string | null {
	if (typeof window === "undefined") {
		return null;
	}
	return window.location.pathname;
}

function countPresentAuthContexts(readContext: (ctx: LeCoffreAuthContext) => boolean): number {
	const contexts: LeCoffreAuthContext[] = ["notary", "customer", "thirdparty"];
	return contexts.filter(readContext).length;
}

/** Distinct families with a token in store and/or cookie (not store count + cookie count). */
function countDistinctAuthContextFamilies(
	hasStoreToken: (ctx: LeCoffreAuthContext) => boolean,
	hasCookieToken: (ctx: LeCoffreAuthContext) => boolean,
): number {
	const contexts: LeCoffreAuthContext[] = ["notary", "customer", "thirdparty"];
	return contexts.filter((ctx) => hasStoreToken(ctx) || hasCookieToken(ctx)).length;
}

function resolveActiveAuthContextFromStoresNeutral(): LeCoffreAuthContext | null {
	const activeFamily = getActiveSessionFamily();
	if (activeFamily && getTokenFromConnectedStoreByContext(activeFamily)) {
		return activeFamily;
	}
	if (getTokenFromConnectedStoreByContext("notary")) {
		return "notary";
	}
	if (getTokenFromConnectedStoreByContext("customer")) {
		return "customer";
	}
	if (getTokenFromConnectedStoreByContext("thirdparty")) {
		return "thirdparty";
	}
	return null;
}

function resolveActiveAuthContextFromCookiesNeutral(): LeCoffreAuthContext | null {
	const activeFamily = getActiveSessionFamily();
	if (activeFamily && getLeCoffreAccessTokenCookieForContext(activeFamily)) {
		return activeFamily;
	}
	if (getLeCoffreNotaryAccessTokenCookie()) {
		return "notary";
	}
	if (getLeCoffreCustomerAccessTokenCookie()) {
		return "customer";
	}
	if (getLeCoffreThirdPartyAccessTokenCookie()) {
		return "thirdparty";
	}
	return null;
}

function logNeutralRouteMultiSessionIfNeeded(pathname: string | null, resolved: LeCoffreAuthContext | null): void {
	const distinctFamilies = countDistinctAuthContextFamilies(
		(ctx) => Boolean(getTokenFromConnectedStoreByContext(ctx)),
		(ctx) => Boolean(getLeCoffreAccessTokenCookieForContext(ctx)),
	);
	if (distinctFamilies <= 1) {
		return;
	}
	const presentStoreCount = countPresentAuthContexts((ctx) => Boolean(getTokenFromConnectedStoreByContext(ctx)));
	const presentCookieCount = countPresentAuthContexts((ctx) => Boolean(getLeCoffreAccessTokenCookieForContext(ctx)));
	LoggerService.warn("⚠️ [SessionContext] Plusieurs familles de session sur route neutre — résolution activeSessionFamily / notary-first", {
		pathname,
		resolved,
		activeSessionFamily: getActiveSessionFamily(),
		distinctFamilies,
		presentStoreCount,
		presentCookieCount,
	});
}

function resolveActiveAuthContextFromStoresForFamily(routeFamily: ReturnType<typeof resolveRouteContextFamily>): LeCoffreAuthContext | null {
	if (routeFamily === "notary") {
		return getTokenFromConnectedStoreByContext("notary") ? "notary" : null;
	}
	if (routeFamily === "client") {
		if (getTokenFromConnectedStoreByContext("customer")) {
			return "customer";
		}
		if (getTokenFromConnectedStoreByContext("thirdparty")) {
			return "thirdparty";
		}
		return null;
	}
	return resolveActiveAuthContextFromStoresNeutral();
}

function resolveActiveAuthContextFromCookiesForFamily(routeFamily: ReturnType<typeof resolveRouteContextFamily>): LeCoffreAuthContext | null {
	if (routeFamily === "notary") {
		return getLeCoffreNotaryAccessTokenCookie() ? "notary" : null;
	}
	if (routeFamily === "client") {
		if (getLeCoffreCustomerAccessTokenCookie()) {
			return "customer";
		}
		if (getLeCoffreThirdPartyAccessTokenCookie()) {
			return "thirdparty";
		}
		return null;
	}
	return resolveActiveAuthContextFromCookiesNeutral();
}

function getUserStoreToken(): string | null {
	if (typeof window === "undefined") return null;
	return UserStore.instance.isConnected() ? UserStore.instance.getAccessToken() : null;
}

function getCustomerStoreToken(): string | null {
	if (typeof window === "undefined") return null;
	return CustomerStore.instance.isConnected() ? CustomerStore.instance.getAccessToken() : null;
}

function getThirdPartyStoreToken(): string | null {
	if (typeof window === "undefined") return null;
	return ThirdPartyStore.instance.isConnected() ? ThirdPartyStore.instance.getAccessToken() : null;
}

const STORE_TOKEN_READERS: Record<LeCoffreAuthContext, AccessTokenReader> = {
	notary: getUserStoreToken,
	customer: getCustomerStoreToken,
	thirdparty: getThirdPartyStoreToken,
};

function readStoreTokenSafely(reader: AccessTokenReader): string | null {
	try {
		return reader();
	} catch {
		return null;
	}
}

export function resolveAuthContextForEndpoint(endpoint?: string | null): LeCoffreAuthContext {
	if (shouldUseActiveSessionTokenForEndpoint(endpoint)) {
		return resolveActiveAuthContextFromSession() ?? "notary";
	}
	return resolveLeCoffreAuthContextFromEndpoint(endpoint) ?? "notary";
}

export function resolveActiveAuthContextFromStores(): LeCoffreAuthContext | null {
	return resolveActiveAuthContextFromStoresNeutral();
}

export function resolveActiveAuthContextFromCookies(): LeCoffreAuthContext | null {
	return resolveActiveAuthContextFromCookiesNeutral();
}

export function resolveActiveAuthContextFromSession(pathname?: string | null): LeCoffreAuthContext | null {
	const path = pathname ?? getBrowserPathnameForAuthContext();
	const routeFamily = resolveRouteContextFamily(path);
	const fromStores = resolveActiveAuthContextFromStoresForFamily(routeFamily);
	if (fromStores) {
		if (routeFamily === "neutral") {
			logNeutralRouteMultiSessionIfNeeded(path, fromStores);
		}
		return fromStores;
	}
	const fromCookies = resolveActiveAuthContextFromCookiesForFamily(routeFamily);
	if (fromCookies && routeFamily === "neutral") {
		logNeutralRouteMultiSessionIfNeeded(path, fromCookies);
	}
	return fromCookies;
}

export function getTokenFromConnectedStoreByContext(context: LeCoffreAuthContext): string | null {
	return readStoreTokenSafely(STORE_TOKEN_READERS[context]);
}

export function getTokenFromAnyConnectedStore(): string | null {
	return (
		getTokenFromConnectedStoreByContext("customer") ??
		getTokenFromConnectedStoreByContext("thirdparty") ??
		getTokenFromConnectedStoreByContext("notary")
	);
}

export function getTokenFromAnyCookie(): string | null {
	return getLeCoffreCustomerAccessTokenCookie() ?? getLeCoffreThirdPartyAccessTokenCookie() ?? getLeCoffreNotaryAccessTokenCookie();
}

function resolveAccessTokenForContext(context: LeCoffreAuthContext): string | null {
	return resolveConsistentAccessToken(
		context,
		getTokenFromConnectedStoreByContext(context),
		getLeCoffreAccessTokenCookieForContext(context),
	);
}

export function resolveAccessTokenForEndpoint(endpoint?: string): { context: LeCoffreAuthContext; token: string | null } {
	const context = resolveAuthContextForEndpoint(endpoint);
	if (shouldUseActiveSessionTokenForEndpoint(endpoint)) {
		return {
			context,
			token: resolveActiveAccessTokenFromSession(),
		};
	}
	return {
		context,
		token: resolveAccessTokenForContext(context),
	};
}

export function resolveActiveAccessTokenFromSession(): string | null {
	const activeContext = resolveActiveAuthContextFromSession();
	if (!activeContext) {
		return null;
	}
	return resolveAccessTokenForContext(activeContext);
}

export function resolveActiveRefreshTokenFromSession(): string | null {
	const activeContext = resolveActiveAuthContextFromSession();
	if (!activeContext) {
		return null;
	}
	return getLeCoffreRefreshTokenCookieForContext(activeContext);
}

/** Notary refresh token only (ignores customer/third-party session priority). */
export function resolveNotaryRefreshTokenFromSession(): string | null {
	return getTokenFromConnectedStoreByContext("notary") ?? getLeCoffreNotaryRefreshTokenCookie();
}

/** Notary access token only (ignores customer/third-party session priority). */
export function resolveNotaryAccessTokenFromSession(): string | null {
	return resolveAccessTokenForContext("notary");
}

/**
 * On notary workspace routes (and any non-client route when a notary JWT exists), prefer notary
 * over a residual customer/third-party cookie — required for notary routes including /my-account.
 */
export function resolveAuthContextForRolePermissionsRefresh(): LeCoffreAuthContext | null {
	const pathname = getBrowserPathnameForAuthContext();
	const routeFamily = resolveRouteContextFamily(pathname);
	if (routeFamily === "client") {
		return resolveActiveAuthContextFromSession(pathname);
	}
	if (getTokenFromConnectedStoreByContext("notary") || getLeCoffreNotaryAccessTokenCookie()) {
		return "notary";
	}
	return resolveActiveAuthContextFromSession(pathname);
}
